// Alertas de um item com data/horário (texto 10, A3). UM componente só, para a Nova tarefa e, depois (A4),
// para todas as abas que têm data e horário.
//  - Sem horário: "Sem alerta" e "⏰ Em um horário específico" (os outros não se aplicam e nem aparecem).
//  - Com horário: "No início", "Antes do início" (5 min, 10 min, 30 min, 1 h) e "Em um horário específico".
//  - Cada alerta tem "Como avisar" (Padrão do app · Alarme (som) só com os sons próprios · Vibração onde o
//    aviso vibra sozinho · Som e vibração · Desligado) e um botão pequeno "Testar".
// O modo fica em AlertaItem.como (sem = padrão do app/da aba) e é usado ao agendar (notifications/rescheduleAll).
import { useState } from 'react';
import { Linking, Pressable, Text, View } from 'react-native';
import type { AbaId, AlertaItem, ModoAviso } from '../store/types';
import { MODOS_AVISO, modoDaAba } from '../lib/avisos';
import { testarAlerta, type ResultadoTeste } from '../notifications';
import { semBloqueio } from '../lib/ausencia';
import { hm, horaTela } from '../lib/date';
import { rotuloDias } from '../lib/alertasData';
import { t } from '../lib/i18n';
import { C, alpha } from './theme';
import { Chip, Field } from './ui';
import { PickerModal } from './Pickers';

/** Minutos de "Antes do início". */
export const ANTES_MIN = [5, 10, 30, 60];
const rotuloMin = (m: number) => (m === 60 ? t('1 h') : t('{n} min', { n: m }));

/** Uma frase com o resultado do "Testar alerta" (texto 10, A2). modo = o modo que foi testado. */
export function textoDoTeste(r: ResultadoTeste, modo: ModoAviso): string {
  switch (r) {
    case 'ok': return modo === 'vibra' ? t('Pronto: o aviso chega em até 5 segundos, só vibrando.')
      : t('Pronto: o aviso chega em até 5 segundos, com som. Se não tocar, confira se o celular está no silencioso.');
    case 'sem-permissao': return t('As notificações do My Planner estão desligadas no celular.');
    case 'som-desligado': return t('O aviso vai chegar, mas o som das notificações do My Planner está desligado no celular.');
    case 'canal-desligado': return t('Este tipo de aviso está desligado nas configurações de notificação do celular.');
    case 'canal-mudo': return t('O aviso vai chegar sem som: o som deste tipo de aviso foi desligado nas configurações do celular.');
    case 'resumo': return t('O aviso vai chegar em silêncio, direto na lista de notificações: ligue os alertas do My Planner nas configurações do celular.');
    case 'nao-perturbe': return t('Agora é horário do Não perturbe do app: o aviso chega em até 5 segundos, sem som nem vibração.');
    case 'desligado': return t('Este aviso está em "Desligado": nada vai tocar.');
    case 'indisponivel': return t('Os avisos do celular funcionam no app instalado.');
    default: return t('Não foi possível testar agora. Tente de novo em alguns segundos.');
  }
}
/** O resultado pede o botão "Abrir configurações do celular"? */
export const testePedeAjustes = (r: ResultadoTeste) => r === 'sem-permissao' || r === 'som-desligado' || r === 'canal-desligado' || r === 'canal-mudo' || r === 'resumo';
/** Abre as configurações do app no celular (a volta não pede a senha do app). */
export const abrirConfiguracoes = () => { semBloqueio(() => Linking.openSettings()).catch(() => {}); };

/** Frase do teste + botão das configurações quando precisa. */
export function ResultadoDoTeste({ r, modo, cor }: { r: ResultadoTeste; modo: ModoAviso; cor: string }) {
  const bom = r === 'ok' || r === 'nao-perturbe';
  return (
    <View style={{ gap: 6 }}>
      <Text style={{ color: bom ? C.ok : C.warn, fontSize: 12 }}>{textoDoTeste(r, modo)}</Text>
      {testePedeAjustes(r) && (
        <Pressable onPress={abrirConfiguracoes} accessibilityRole="button" style={({ pressed }) => ({ alignSelf: 'flex-start', paddingVertical: 6, paddingHorizontal: 12,
          borderRadius: 14, borderWidth: 1, borderColor: cor, backgroundColor: pressed ? alpha(cor, 0.15) : 'transparent' })}>
          <Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>{'⚙️ ' + t('Abrir configurações do celular')}</Text>
        </Pressable>
      )}
    </View>
  );
}

/** Botão pequeno "▶ Testar" (testa já, do jeito do modo). */
export function BotaoTestar({ cor, onPress, ocupado }: { cor: string; onPress: () => void; ocupado?: boolean }) {
  return (
    <Pressable onPress={onPress} disabled={ocupado} hitSlop={8} accessibilityRole="button" accessibilityLabel={t('Testar alerta')}
      style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 4, paddingHorizontal: 10, borderRadius: 14,
        borderWidth: 1, borderColor: cor, opacity: ocupado ? 0.5 : 1, backgroundColor: pressed ? alpha(cor, 0.15) : 'transparent' })}>
      <Text style={{ color: cor, fontSize: 12 }}>🔔</Text>
      <Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>{t('Testar')}</Text>
    </Pressable>
  );
}

type Props = {
  itens: AlertaItem[];
  onChange: (itens: AlertaItem[]) => void;
  /** O item tem horário? Sem horário só vale "Em um horário específico". */
  temHorario: boolean;
  /** Hora sugerida para um alerta novo "Em um horário específico" (sem = 09:00). */
  horaPadrao?: string | null;
  cor: string;
  /** Aba do item: o "Padrão do app" usa o modo da aba (Ajustes > Abas). */
  aba?: AbaId | null;
  /** Item com DATA (A4: fichas, documentos, garantias, contas...): cada "Em um horário específico" escolhe também
   *  quantos dias antes da data (estas são as opções; 0 = no dia). Sem = a hora é no próprio dia (tarefas). */
  dias?: number[];
};

export function AlertasEditor({ itens, onChange, temHorario, horaPadrao, cor, aba, dias }: Props) {
  const [hora, setHora] = useState<{ i: number; valor: string } | null>(null);
  const [teste, setTeste] = useState<{ i: number; r: ResultadoTeste; modo: ModoAviso } | null>(null);
  const [testando, setTestando] = useState(false);
  // sem horário, "No início" e "Antes do início" não valem: nem aparecem (continuam guardados, se a tarefa ganhar horário)
  const visiveis = itens.map((a, i) => ({ a, i })).filter(x => temHorario || x.a.tipo === 'hora');
  const mudar = (i: number, p: Partial<AlertaItem>) => onChange(itens.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const tirar = (i: number) => { setTeste(null); onChange(itens.filter((_, j) => j !== i)); };
  const asTime = (h: string) => { const d = new Date(); const [a, b] = h.split(':').map(Number); d.setHours(a || 0, b || 0, 0, 0); return d; };

  function adicionar(tipo: AlertaItem['tipo']) {
    setTeste(null);
    if (tipo === 'inicio') { if (!itens.some(a => a.tipo === 'inicio' || (a.tipo === 'antes' && !a.min))) onChange([...itens, { tipo }]); return; }
    if (tipo === 'antes') {
      const livre = ANTES_MIN.find(m => !itens.some(a => a.tipo === 'antes' && a.min === m)) ?? 10;
      onChange([...itens, { tipo, min: livre }]);
      return;
    }
    const valor = horaPadrao && /^\d{2}:\d{2}$/.test(horaPadrao) ? horaPadrao : '09:00';
    if (dias) {
      // com data: o primeiro dia ainda sem alerta nesta hora (no dia, 1 dia antes...)
      const livre = dias.filter(n => n >= 0).find(n => !itens.some(a => a.tipo === 'hora' && (a.dias ?? 0) === n && a.hora === valor)) ?? 0;
      onChange([...itens, { tipo, hora: valor, dias: livre }]);
    } else onChange([...itens, { tipo, hora: valor }]);
    setHora({ i: itens.length, valor });
  }

  async function testar(i: number, como: ModoAviso | undefined) {
    const modo = como ?? modoDaAba(aba);
    setTestando(true);
    try { setTeste({ i, r: await testarAlerta(modo), modo }); }
    finally { setTestando(false); }
  }

  return (
    <View style={{ gap: 8 }}>
      <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        <Chip cor={cor} small on={!visiveis.length} label={t('Sem alerta')} onPress={() => { setTeste(null); onChange(temHorario ? [] : itens.filter(a => a.tipo !== 'hora')); }} />
        {temHorario && <Chip cor={cor} small on={false} label={'+ ' + t('No início')} onPress={() => adicionar('inicio')} />}
        {temHorario && <Chip cor={cor} small on={false} label={'+ ' + t('Antes do início')} onPress={() => adicionar('antes')} />}
        <Chip cor={cor} small on={false} label={'⏰ ' + t('Em um horário específico')} onPress={() => adicionar('hora')} />
      </View>

      {visiveis.map(({ a, i }) => (
        <View key={i} style={{ gap: 8, backgroundColor: C.card2, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 8 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text style={{ fontSize: 15 }}>🔔</Text>
            {a.tipo === 'hora' ? (<>
              <Text style={{ color: C.text }}>{t('Às')}</Text>
              <Chip cor={cor} on small label={a.hora ? horaTela(a.hora) : '--:--'} onPress={() => setHora({ i, valor: a.hora ?? horaPadrao ?? '09:00' })} />
              <Text style={{ color: C.sub, fontSize: 12, flex: 1 }}>{dias ? rotuloDias(a.dias ?? 0) : t('hora fixa no dia')}</Text>
            </>) : a.tipo === 'antes' && a.min ? (
              <Text style={{ color: C.text, flex: 1, fontWeight: '600' }}>{t('Antes do início')}</Text>
            ) : (
              <Text style={{ color: C.text, flex: 1, fontWeight: '600' }}>{t('No início')}</Text>
            )}
            <Pressable onPress={() => tirar(i)} hitSlop={8} accessibilityRole="button" accessibilityLabel={t('Excluir')}>
              <Text style={{ color: C.sub, fontSize: 16 }}>✕</Text>
            </Pressable>
          </View>

          {a.tipo === 'hora' && !!dias && (
            <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
              {[...new Set([...dias, a.dias ?? 0])].sort((x, y) => x - y).map(n => (
                <Chip key={n} cor={cor} small on={(a.dias ?? 0) === n} label={rotuloDias(n)} onPress={() => mudar(i, { dias: n })} />))}
            </View>
          )}

          {a.tipo === 'antes' && !!a.min && (
            <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {ANTES_MIN.map(m => <Chip key={m} cor={cor} small on={a.min === m} label={rotuloMin(m)} onPress={() => mudar(i, { min: m })} />)}
              {/* um valor de antes (ex.: "Avisar a hora de sair", 15 ou 45 min) continua aparecendo e dá para digitar outro */}
              <Field value={ANTES_MIN.includes(a.min) ? '' : String(a.min)} keyboardType="number-pad" maxLength={4} placeholder={t('outro')} selectTextOnFocus
                style={{ width: 70, textAlign: 'center', paddingVertical: 5, backgroundColor: C.card }}
                onChangeText={v => { const n = Math.min(1440, Number(v.replace(/\D/g, '')) || 0); if (n) mudar(i, { min: n }); }} />
              <Text style={{ color: C.sub, fontSize: 12 }}>{t('min antes do início')}</Text>
            </View>
          )}

          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text style={{ color: C.sub, fontSize: 12, flex: 1 }}>{t('Como avisar')}</Text>
            <BotaoTestar cor={cor} ocupado={testando} onPress={() => testar(i, a.como)} />
          </View>
          <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
            <Chip cor={cor} small on={!a.como} label={t('Padrão do app')} onPress={() => { setTeste(null); mudar(i, { como: undefined }); }} />
            {MODOS_AVISO.map(([m, l]) => <Chip key={m} cor={cor} small on={a.como === m} label={t(l)} onPress={() => { setTeste(null); mudar(i, { como: m }); }} />)}
          </View>
          {teste?.i === i && <ResultadoDoTeste r={teste.r} modo={teste.modo} cor={cor} />}
        </View>
      ))}

      {hora && <PickerModal mode="time" cor={cor} value={asTime(hora.valor)} onClose={() => setHora(null)}
        onPick={d => { mudar(hora.i, { hora: hm(d) }); setHora(null); }} />}
    </View>
  );
}
